import { fetchWithAuth } from '../lib/fetchWithAuth';

const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, '');

export type WwwModerationStatus = 'draft' | 'pending_review' | 'approved' | 'rejected';

export type WwwAdminJob = {
  id: string;
  slug: string;
  title: string;
  overview: string;
  jobType: string;
  location: string;
  schedule?: string | null;
  pay: string;
  duties: string[];
  requirements: string[];
  benefits?: string[] | null;
  additionalInfo?: string[] | null;
  tags?: string[] | null;
  imageUrl?: string | null;
  postedAt?: string | null;
  closingAt?: string | null;
  isPublished: boolean;
  moderationStatus: WwwModerationStatus;
  sanitizedHtml?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export type CreateWwwAdminJobInput = {
  slug: string;
  title: string;
  overview: string;
  jobType: string;
  location: string;
  pay: string;
  schedule?: string;
  duties?: string[];
  requirements?: string[];
  benefits?: string[];
  additionalInfo?: string[];
  tags?: string[];
  imageUrl?: string;
  postedAt?: string;
  closingAt?: string;
  isPublished?: boolean;
  moderationStatus?: WwwModerationStatus;
  sanitizedHtml?: string;
};

export type UpdateWwwAdminJobInput = Partial<CreateWwwAdminJobInput>;

async function parseError(response: Response, fallbackMessage: string) {
  const text = await response.text().catch(() => '');
  if (!text) return fallbackMessage;
  try {
    const json = JSON.parse(text) as { message?: string; detail?: string };
    return json.message ?? json.detail ?? fallbackMessage;
  } catch {
    return text;
  }
}

export async function fetchWwwAdminJobs(): Promise<WwwAdminJob[]> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to load WWW admin jobs.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/www/jobs`);
  if (!response.ok) {
    throw new Error(await parseError(response, `Failed to load WWW jobs (${response.status})`));
  }
  const payload = (await response.json()) as { jobs?: WwwAdminJob[] };
  return payload.jobs ?? [];
}

export async function createWwwAdminJob(input: CreateWwwAdminJobInput): Promise<WwwAdminJob> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to create WWW jobs.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/www/jobs`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new Error(await parseError(response, `Failed to create WWW job (${response.status})`));
  }
  return (await response.json()) as WwwAdminJob;
}

export async function updateWwwAdminJob(id: string, input: UpdateWwwAdminJobInput): Promise<WwwAdminJob> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to update WWW jobs.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/www/jobs/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new Error(await parseError(response, `Failed to update WWW job (${response.status})`));
  }
  return (await response.json()) as WwwAdminJob;
}

export async function deleteWwwAdminJob(id: string): Promise<void> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to delete WWW jobs.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/admin/www/jobs/${id}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error(await parseError(response, `Failed to delete WWW job (${response.status})`));
  }
}

